import { useState, useRef, useEffect } from 'react';
import { FiSmile, FiMessageSquare, FiTrash2, FiFlag } from 'react-icons/fi';
import { BsPinAngle, BsPinAngleFill } from 'react-icons/bs';
import { cn } from '../../utils/helpers';

// A short fixed set rather than a full picker: it covers the overwhelming
// majority of real use and avoids pulling in an emoji-data dependency.
const QUICK_REACTIONS = ['👍', '❤️', '🙏', '🎉', '😂', '👀'];

/**
 * Floating action bar for a single message.
 *
 * Revealed by hover on a pointer device and by long-press on a touch one. The
 * mobile apps render this same React UI inside a WebView, so long-press is what
 * makes the bar reachable there — there is no separate native implementation to
 * keep in step.
 */
export default function MessageActions({
  visible,
  isMine,
  side = 'right',
  isPinned,
  onReact,
  onReply,
  onTogglePin,
  onDelete,
  onReport,
  onDismiss,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!visible) setPickerOpen(false);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className={cn(
        'absolute -top-3 z-10 flex items-center gap-0.5 p-0.5 pointer-events-auto',
        'bg-white border border-neutral-300 rounded-full shadow-md shadow-primary/10',
        // Sits beside the bubble it belongs to: past the avatar column on the
        // left, flush with the panel edge on the member's own side.
        side === 'right' ? 'right-1' : 'left-9'
      )}
      // Keeps a long-press from also selecting text or firing the row beneath.
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div className="relative">
        <button
          onClick={() => setPickerOpen((open) => !open)}
          className="p-1.5 rounded-full hover:bg-highlight text-primary/55 hover:text-primary transition-colors"
          aria-label="Add reaction"
          aria-expanded={pickerOpen}
        >
          <FiSmile className="w-3.5 h-3.5" />
        </button>

        {pickerOpen && (
          <>
            <div
              className="fixed inset-0 z-[56]"
              onClick={() => setPickerOpen(false)}
              aria-hidden="true"
            />
            <div
              className={cn(
                'absolute bottom-full mb-1 z-[57] flex gap-0.5 p-1',
                'bg-white rounded-[10px] shadow-lg shadow-primary/10 border border-neutral-300',
                // Opens away from the panel edge the bar is pinned to.
                side === 'right' ? 'right-0' : 'left-0'
              )}
            >
              {QUICK_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    setPickerOpen(false);
                    onReact(emoji);
                    onDismiss?.();
                  }}
                  className="p-1 rounded-lg hover:bg-highlight text-base leading-none transition-colors"
                  aria-label={`React with ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <button
        onClick={() => { onReply(); onDismiss?.(); }}
        className="p-1.5 rounded-full hover:bg-highlight text-primary/55 hover:text-primary transition-colors"
        aria-label="Reply in thread"
        title="Reply in thread"
      >
        <FiMessageSquare className="w-3.5 h-3.5" />
      </button>

      <button
        onClick={() => { onTogglePin(); onDismiss?.(); }}
        className={cn(
          'p-1.5 rounded-full hover:bg-highlight transition-colors',
          isPinned ? 'text-secondary' : 'text-primary/55 hover:text-primary'
        )}
        aria-label={isPinned ? 'Unpin message' : 'Pin message'}
        title={isPinned ? 'Unpin from top' : 'Pin to top'}
      >
        {isPinned ? <BsPinAngleFill className="w-3.5 h-3.5" /> : <BsPinAngle className="w-3.5 h-3.5" />}
      </button>

      {isMine ? (
        <button
          onClick={() => { onDelete(); onDismiss?.(); }}
          className="p-1.5 rounded-full hover:bg-alert/10 text-primary/55 hover:text-alert transition-colors"
          aria-label="Delete message"
          title="Delete message"
        >
          <FiTrash2 className="w-3.5 h-3.5" />
        </button>
      ) : (
        // Reporting your own message is meaningless, so the slot carries
        // whichever action applies rather than showing both.
        <button
          onClick={() => { onReport(); onDismiss?.(); }}
          className="p-1.5 rounded-full hover:bg-alert/10 text-primary/55 hover:text-alert transition-colors"
          aria-label="Report message"
          title="Report to a moderator"
        >
          <FiFlag className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
